import React, { FC, ReactNode, useMemo, useState } from 'react';

import { Checkbox, Empty, Segmented } from 'antd';

import { TCrmSummary } from '../../../../shared/common/api/endpoints/crmAnalyticsApi';
import EChart from '../../../../shared/common/components/reports/EChart';
import {
  commissionChartOption,
  movementChartOption,
  revenueChartOption,
  statusChartOption,
} from '../chartOptions';
import { TRevenueMode } from '../constants';
import styles from '../CrmAnalytics.module.scss';

interface Props {
  summary: TCrmSummary | null;
  onlyReal: boolean;
}

const ChartCard: FC<{ title: string; hint: string; extra?: ReactNode; wide?: boolean }> = ({
  title,
  hint,
  extra,
  wide,
  children,
}) => (
  <div className={wide ? `${styles.chartCard} ${styles.chartCardWide}` : styles.chartCard}>
    <div className={styles.chartHeader}>
      <div>
        <div className={styles.chartTitle}>{title}</div>
        <div className={styles.chartHint}>{hint}</div>
      </div>
      {extra}
    </div>
    {children}
  </div>
);

const OverviewTab: FC<Props> = ({ summary, onlyReal }) => {
  const [ showDead, setShowDead ] = useState(false);
  const [ revenueMode, setRevenueMode ] = useState<TRevenueMode>('cash');

  const rows = summary?.rows ?? [];
  const granularity = summary?.granularity ?? 'month';

  const statusOption = useMemo(() => statusChartOption(rows, granularity, showDead), [ rows, granularity, showDead ]);
  const revenueOption = useMemo(
    () => revenueChartOption(rows, granularity, revenueMode, onlyReal),
    [ rows, granularity, revenueMode, onlyReal ],
  );
  const commissionOption = useMemo(() => commissionChartOption(rows, granularity), [ rows, granularity ]);
  const movementOption = useMemo(() => movementChartOption(rows, granularity), [ rows, granularity ]);

  if (!summary) return <Empty description="Нет данных" />;

  return (
    <div className={styles.charts}>
      <ChartCard
        wide
        title="Школы по статусам"
        hint="Сколько школ в каждом статусе на последний день отрезка (по ночной детализации)"
        extra={(
          <Checkbox checked={showDead} onChange={(e) => setShowDead(e.target.checked)}>
            Показать неживые льготные
          </Checkbox>
        )}
      >
        <EChart option={statusOption} style={{ height: 360 }} maxDataPoints={0} lazyLoad={false} />
      </ChartCard>

      <ChartCard
        title={revenueMode === 'cash' ? 'Поступления' : 'Начислено'}
        hint={revenueMode === 'cash'
          ? 'Деньги по дате платежа'
          : 'Списания за дни доступа — сколько сервис отработал'}
        extra={(
          <Segmented
            size="small"
            value={revenueMode}
            onChange={(value) => setRevenueMode(value as TRevenueMode)}
            options={[
              { label: 'Поступления', value: 'cash' },
              { label: 'Начислено', value: 'accrual' },
            ]}
          />
        )}
      >
        <EChart option={revenueOption} style={{ height: 320 }} maxDataPoints={0} lazyLoad={false} />
      </ChartCard>

      <ChartCard
        title="Комиссия партнёров"
        hint="Начислено партнёрам по платежам за отрезок, по дате платежа. Из выручки не вычитается"
      >
        <EChart option={commissionOption} style={{ height: 320 }} maxDataPoints={0} lazyLoad={false} />
      </ChartCard>

      <ChartCard
        wide
        title="Движение платящих школ"
        hint="Вверх — начали платить и вернулись после перерыва больше 30 дней, вниз — ушли: 30 дней без оплаченного доступа. Последние 30 дней досчитываются"
      >
        <EChart option={movementOption} style={{ height: 300 }} maxDataPoints={0} lazyLoad={false} />
      </ChartCard>
    </div>
  );
};

export default OverviewTab;
